<script setup lang="ts">
import { computed } from 'vue';

import { RESOURCE_META } from '@waldermarch/shared';

import { RESOURCE_ART } from '@/assets/art/art';
import { UI_ICONS } from '@/assets/sprites/manifest';
import { titleise } from '@/utils/format';

const props = withDefaults(
  defineProps<{
    /** A resource key, or 'population'. */
    name: string;
    size?: number;
  }>(),
  { size: 30 },
);

/**
 * Prefer the photographic render, fall back to the framed sprite-sheet icon.
 * Everything in the resource bar has a render now, but the fallback stays so a
 * new entry added ahead of its art degrades instead of breaking.
 */
const art = computed(() => RESOURCE_ART[props.name]);
const src = computed(() => art.value ?? UI_ICONS[props.name]);

const label = computed(() => {
  const meta = RESOURCE_META[props.name as keyof typeof RESOURCE_META];
  return meta ? meta.displayName : titleise(props.name);
});
</script>

<template>
  <img
    v-if="src"
    class="resource-icon"
    :class="{ 'is-framed': !art }"
    :src="src"
    :width="props.size"
    :height="props.size"
    :alt="label"
    draggable="false"
  />
</template>

<style scoped>
.resource-icon {
  display: block;
  flex: none;
  object-fit: contain;
  user-select: none;
  filter: drop-shadow(0 1px 2px rgb(0 0 0 / 55%));
}

/* The framed icons carry their own border; the shadow would double it up. */
.resource-icon.is-framed {
  border-radius: 2px;
  filter: none;
}
</style>
